/* =========================================================================
   telemetry-tooltip.utils.ts
   Viewport-boundary-aware tooltip placement helpers.
   ========================================================================= */

// Passive global mouse tracking for accurate viewport-relative placement
let lastClientX = 0;
let lastClientY = 0;

if (typeof window !== 'undefined') {
  window.addEventListener(
    'mousemove',
    (e) => {
      lastClientX = e.clientX;
      lastClientY = e.clientY;
    },
    { passive: true }
  );
}

/**
 * Calculates adaptive, boundary-safe tooltip coordinates that guarantee
 * the tooltip box remains 100% visible inside the browser window.
 * Eliminates top clipping (e.g. behind browser tabs) and left/right cutoff
 * during deep zoom or hovering near edge data points.
 */
export function calculateViewportBoundedTooltipPosition(
  point: number[],
  params: any,
  dom: HTMLElement,
  rect: any,
  size: { contentSize: [number, number]; viewSize: [number, number] }
): [number, number] {
  const [boxW, boxH] = size?.contentSize || [220, 120];
  const [mouseX, mouseY] = point || [0, 0];

  const winW = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const winH = typeof window !== 'undefined' ? window.innerHeight : 1080;
  const margin = 12;

  // Determine canvas origin relative to window viewport
  const curClientX = lastClientX > 0 ? lastClientX : mouseX;
  const curClientY = lastClientY > 0 ? lastClientY : mouseY;

  const canvasLeft = curClientX - mouseX;
  const canvasTop = curClientY - mouseY;

  // 1. Horizontal placement:
  // Prefer placing to the right of cursor; flip to left if it exceeds viewport right edge
  let screenX = curClientX + 16;
  if (screenX + boxW > winW - margin) {
    screenX = curClientX - boxW - 16;
  }
  // Clamp inside safe window margins
  screenX = Math.max(margin, Math.min(screenX, winW - boxW - margin));

  // 2. Vertical placement:
  // Center vertically relative to cursor or offset slightly
  let screenY = curClientY - Math.min(40, boxH / 2);
  // Prevent clipping above the top of the browser window (solving outside-window cutoff)
  if (screenY < margin) {
    screenY = margin;
  }
  // Prevent clipping below the bottom of the browser window
  if (screenY + boxH > winH - margin) {
    screenY = Math.max(margin, winH - boxH - margin);
  }

  // Convert target screen coordinate back to canvas-relative coordinates for ECharts moveTo()
  const targetX = screenX - canvasLeft;
  const targetY = screenY - canvasTop;

  return [targetX, targetY];
}
